/* Fechas de eventos.
   Un evento tiene 0..n filas en events_dates (initialDate, maxDate). Pueden ser varios periodos separados entre sí,
   incluso solapados; no se asume un único intervalo continuo. El módulo de Eventos interpreta esas fechas como el día
   calendario local del navegador (isoToJsDate), y aquí se hace lo mismo para que ambos muestren los mismos días. */

const pad = (value) => String(value).padStart(2, '0');

const formatYmd = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// 'YYYY-MM-DD' del día calendario de un valor de fecha, o null si el valor no es una fecha válida
export const toCalendarDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return formatYmd(date);
};

const addDays = (ymd, days) => {
  const [year, month, day] = ymd.split('-').map(Number);
  return formatYmd(new Date(year, month - 1, day + days));
};

// Periodos válidos de un evento. Una fecha con valores inválidos o con fin anterior al inicio se descarta, no se corrige.
export const getEventPeriods = (event) => (Array.isArray(event?.dates) ? event.dates : [])
  .map((date) => ({ from: toCalendarDate(date?.initialDate), to: toCalendarDate(date?.maxDate) }))
  .filter((period) => period.from && period.to && period.from <= period.to);

// Une periodos que se solapan o son consecutivos; los que quedan separados siguen siendo periodos distintos
export const mergePeriods = (periods) => {
  const sorted = [...periods].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
  return sorted.reduce((merged, period) => {
    const last = merged[merged.length - 1];
    if (last && period.from <= addDays(last.to, 1)) {
      if (period.to > last.to) last.to = period.to;
      return merged;
    }
    merged.push({ ...period });
    return merged;
  }, []);
};

/* Rango que usa el botón "Usar fechas del evento".
   Con varios eventos o varias fechas se toma el rango que las abarca a todas (del primer inicio al último fin).
   Si hay huecos entre periodos, el rango incluye días sin actividad de los eventos: no cambia las cifras porque el filtro
   de eventos sigue limitando las órdenes a los eventos elegidos. `periods` lista los periodos reales para informarlos. */
export const getEventsDateRange = (events, selectedIds) => {
  const wanted = new Set((selectedIds || []).map(String));
  const selected = (events || []).filter((event) => wanted.has(String(event.id)));

  const periods = [];
  const eventsWithoutDates = [];
  selected.forEach((event) => {
    const eventPeriods = getEventPeriods(event);
    if (eventPeriods.length === 0) eventsWithoutDates.push({ id: event.id, name: event.name });
    else periods.push(...eventPeriods);
  });

  const merged = mergePeriods(periods);
  return {
    range: merged.length ? { from: merged[0].from, to: merged[merged.length - 1].to } : null,
    periods: merged,
    hasGaps: merged.length > 1,
    eventsWithoutDates,
    selectedCount: selected.length,
  };
};

// 'YYYY-MM-DD' -> 'DD/MM/YYYY' para mostrar
export const formatYmdForDisplay = (ymd) => {
  if (!ymd) return '';
  const [year, month, day] = ymd.split('-');
  return `${day}/${month}/${year}`;
};
